import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import styles from './BoardTools.module.css';
import { useCanvas } from '../../../context/canvasContext';
import { useDocument } from '../../../context/documentInfoContex';
import ChangesDetector from '../../ChangesDetector';
import PrintBoard from '../../Print/printBoard';
import ToolButton, { ColorToolButton } from '../../ToolButton/ToolButton';
import { DownloadIcon, FillIcon, ClearIcon, PaletteIcon, BorderIcon } from '../../ToolButton/icons';

const NO_COLORS = { background: '', fill: '', stroke: '' };

/**
 * The canvas background and the selected object's fill and stroke, kept
 * current across local, remote and undo changes (all of which re-render).
 */
const useCanvasColors = (canvasRef) => {
    const [colors, setColors] = useState(NO_COLORS);
    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return undefined;
        const sync = () => {
            const active = canvas.getActiveObject?.();
            setColors({
                background: canvas.backgroundColor || '',
                fill: (typeof active?.fill === 'string' && active.fill) || '',
                stroke: (typeof active?.stroke === 'string' && active.stroke) || '',
            });
        };
        sync();
        canvas.on('after:render', sync);
        return () => canvas.off('after:render', sync);
    }, [canvasRef]);
    return colors;
};

/** The board tools in the header: print and download for everyone, the colour tools for editors. */
const BoardTools = () => {
    const { id } = useParams();
    const { isReadOnly } = useDocument();
    const { canvasRef, recolorObjects, recolorBG, recolorStroke, convertCanvasToImage, clearCanvas } = useCanvas();
    const [selectedObject, setSelectedObject] = useState(null);
    const colors = useCanvasColors(canvasRef);
    const canRecolorSelection = !isReadOnly && selectedObject && selectedObject.type !== 'image';

    return (
        <div className={styles.tools}>
            <PrintBoard />
            <a id="download-image-link" href="download-link" download onClick={convertCanvasToImage}>
                <ToolButton title="Download as image"><DownloadIcon /></ToolButton>
            </a>
            {!isReadOnly && (
                <>
                    <ColorToolButton
                        title="Change canvas colour"
                        icon={<FillIcon fill={colors.background} />}
                        value={colors.background}
                        onChange={(color) => recolorBG(id, color)}
                    />
                    <ToolButton title="Clear canvas" onClick={clearCanvas}><ClearIcon /></ToolButton>
                </>
            )}
            {canRecolorSelection && (
                <>
                    <ColorToolButton title="Change shape colour" icon={<PaletteIcon fill={colors.fill} />} value={colors.fill} onChange={recolorObjects} />
                    <ColorToolButton title="Change border colour" icon={<BorderIcon stroke={colors.stroke} />} value={colors.stroke} onChange={recolorStroke} />
                </>
            )}
            <ChangesDetector handleDetect={setSelectedObject} selectedObject={selectedObject} />
        </div>
    );
};

export default BoardTools;
